import React, { useState } from 'react';
import './YouTubeEmbed.css';

function YouTubeEmbed({ module }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [showThumbnail, setShowThumbnail] = useState(true);

  const { 
    videoId, 
    title, 
    aspectRatio = '16:9', 
    autoplay = false, 
    showControls = true, 
    startTime = 0 
  } = module;

  if (!videoId || typeof videoId !== 'string' || videoId.length !== 11) {
    return null;
  }

  // Build YouTube embed URL with parameters
  const getEmbedUrl = () => {
    const baseUrl = `https://www.youtube-nocookie.com/embed/${videoId}`;
    const params = new URLSearchParams();
    
    if (autoplay && isLoaded) params.append('autoplay', '1');
    if (!showControls) params.append('controls', '0');
    if (startTime > 0) params.append('start', startTime.toString());
    params.append('playsinline', '1');
    try {
      if (typeof window !== 'undefined' && window.location?.origin) {
        params.append('origin', window.location.origin);
      }
    } catch (_) {}
    
    // Additional YouTube embed parameters for better experience
    params.append('rel', '0'); // Don't show related videos from other channels
    params.append('modestbranding', '1'); // Remove YouTube logo
    params.append('fs', '1'); // Allow fullscreen
    
    return `${baseUrl}?${params.toString()}`;
  };

  // Get thumbnail URL from YouTube
  const getThumbnailUrl = () => {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  // Handle aspect ratio styling
  const getAspectRatioStyle = () => {
    switch (aspectRatio) {
      case '4:3':
        return { paddingBottom: '75%' }; // 3/4 = 0.75
      case '21:9':
        return { paddingBottom: '42.86%' }; // 9/21 ≈ 0.4286
      case '16:9':
      default:
        return { paddingBottom: '56.25%' }; // 9/16 = 0.5625
    }
  };

  const handleThumbnailClick = () => {
    setShowThumbnail(false);
    setIsLoaded(true);
  };

  return (
    <div className="module youtube-module">
      <div 
        className="youtube-embed-container" 
        style={getAspectRatioStyle()}
      >
        {showThumbnail ? (
          <div className="youtube-thumbnail" onClick={handleThumbnailClick}>
            <img 
              src={getThumbnailUrl()} 
              alt={title || `YouTube video ${videoId}`}
              loading="lazy"
            />
            <div className="youtube-play-button">
              <svg 
                width="68" 
                height="48" 
                viewBox="0 0 68 48" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.63 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z" 
                  fill="#FF0000"
                />
                <path 
                  d="M45 24L27 14v20l18-10z" 
                  fill="#FFF"
                />
              </svg>
            </div>
          </div>
        ) : (
          <iframe
            src={getEmbedUrl()}
            title={title || `YouTube video player - ${videoId}`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        )}
      </div>
      {title && (
        <div className="youtube-title">
          <h3>{title}</h3>
        </div>
      )}
    </div>
  );
}

export default YouTubeEmbed; 